import React from 'react';
import { Icon } from '@iconify/react';
import { Avatar, Chip } from '@heroui/react';
import { Sidebar, useSidebar } from '@heroui-pro/react';
import { BuildCommit } from '@/components/buildCommit';
import { ModeBadge } from '@/components/modeBadge';
import { useLogoutMutation } from '@/api/queries/auth';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { getUser } from '@/utils';
import type { UserRole } from '@/api/models';
import {
    findActiveNavigationItem,
    isNavigationItemActive,
    isNavigationItemVisible,
    SECURED_NAVIGATION_GROUPS,
    SECURED_NAVIGATION_ITEMS,
    type SecuredNavigationGroup,
    type SecuredNavigationItem,
} from '../navigation';
import { DashboardLanguageToggle } from './DashboardLanguageToggle';
import { DashboardThemeToggle } from './DashboardThemeToggle';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Role → `layout.profile.roles.*` key suffix. The API's role names are
 * PascalCase; the locale keys are camelCase, so the mapping is spelled out
 * rather than lower-casing the first letter by hand at the call site.
 */
const PROFILE_ROLE_KEY: Record<UserRole, string> = {
    BranchAdmin: 'branchAdmin',
    OrgAdmin: 'orgAdmin',
    Seller: 'seller',
};

/** Shown in place of a missing name/role — never a raw i18n key. */
const PROFILE_FALLBACK = '—';

type DashboardSidebarProps = {
    pathname: string;
};

type SidebarContentsProps = {
    idPrefix?: string;
    pathname: string;
    showThemeToggle?: boolean;
};

type SidebarNavItemProps = {
    idPrefix: string;
    item: SecuredNavigationItem;
    pathname: string;
};

type SidebarNavGroupProps = {
    group: SecuredNavigationGroup;
    idPrefix: string;
    pathname: string;
};

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    pathname,
}) => {
    return (
        <>
            <Sidebar data-testid="dashboard-sidebar" id="dashboard-sidebar">
                <SidebarContents pathname={pathname} />
            </Sidebar>
            <Sidebar.Mobile
                data-testid="dashboard-sidebar-mobile"
                id="dashboard-sidebar-mobile"
            >
                <SidebarContents
                    idPrefix="mobile-"
                    pathname={pathname}
                    showThemeToggle
                />
            </Sidebar.Mobile>
        </>
    );
};

const SidebarContents: React.FC<SidebarContentsProps> = ({
    idPrefix = '',
    pathname,
    showThemeToggle = false,
}) => {
    const { t } = useTranslation('components');
    const navigate = useNavigate();
    const logoutMutation = useLogoutMutation();

    // Role-gate top-level items (e.g. Team is admin-only). Read once from the
    // session; the sidebar re-mounts on login/logout so this stays in step.
    const user = getUser();
    const role = user?.role ?? null;

    // The profile block shows the signed-in principal. The name is user data
    // (never translated); only the role label goes through i18n.
    const profileName = user?.name?.trim() || PROFILE_FALLBACK;
    const profileRole = role
        ? t(`layout.profile.roles.${PROFILE_ROLE_KEY[role]}`)
        : PROFILE_FALLBACK;
    const visibleItems = React.useMemo(
        () =>
            SECURED_NAVIGATION_ITEMS.filter((item) =>
                isNavigationItemVisible(item, role),
            ),
        [role],
    );

    const onLogout = React.useCallback(() => {
        // Best-effort revoke the refresh token server-side, then clear and
        // leave. The mutation clears the local session in its own onSettled
        // (which runs first), so by the time we navigate the guard already
        // sees no session — no flash back into the app, no redirect loop.
        // makeApiRequest never rejects, so onSettled always fires (even
        // offline) and the navigation can't get stuck.
        logoutMutation.mutate(undefined, {
            onSettled: () => navigate(PATH.LOGIN, { replace: true }),
        });
    }, [logoutMutation, navigate]);

    const defaultExpandedKeys = React.useMemo(
        () =>
            SECURED_NAVIGATION_GROUPS.filter((group) =>
                group.items.some((item) =>
                    isNavigationItemActive(item, pathname),
                ),
            ).map(
                (group) =>
                    `${idPrefix}dashboard-sidebar-group-item-${group.slug}`,
            ),
        [idPrefix, pathname],
    );

    return (
        <>
            <Sidebar.Header
                data-testid={`${idPrefix}dashboard-sidebar-header`}
                id={`${idPrefix}dashboard-sidebar-header`}
            >
                <div
                    className="flex items-center gap-3 px-1 py-1"
                    {...getNodeTestIdProp(
                        `${idPrefix}dashboard-sidebar-profile`,
                    )}
                    id={`${idPrefix}dashboard-sidebar-profile`}
                >
                    <Avatar
                        className="size-9"
                        data-testid={`${idPrefix}dashboard-sidebar-avatar`}
                        id={`${idPrefix}dashboard-sidebar-avatar`}
                    >
                        <Avatar.Image
                            alt={t('layout.profile.avatarAlt')}
                            data-testid={`${idPrefix}dashboard-sidebar-avatar-image`}
                            id={`${idPrefix}dashboard-sidebar-avatar-image`}
                            src="https://heroui-assets.nyc3.cdn.digitaloceanspaces.com/avatars/blue-light.jpg"
                        />
                        <Avatar.Fallback
                            data-testid={`${idPrefix}dashboard-sidebar-avatar-fallback`}
                            id={`${idPrefix}dashboard-sidebar-avatar-fallback`}
                        >
                            CA
                        </Avatar.Fallback>
                    </Avatar>
                    <div
                        className="flex min-w-0 flex-col"
                        data-sidebar="label"
                        {...getNodeTestIdProp(
                            `${idPrefix}dashboard-sidebar-profile-copy`,
                        )}
                        id={`${idPrefix}dashboard-sidebar-profile-copy`}
                    >
                        <span
                            className="text-sm font-medium leading-tight text-foreground"
                            {...getNodeTestIdProp(
                                `${idPrefix}dashboard-sidebar-profile-name`,
                            )}
                            id={`${idPrefix}dashboard-sidebar-profile-name`}
                        >
                            {profileName}
                        </span>
                        <span
                            className="text-xs font-medium leading-tight text-muted"
                            {...getNodeTestIdProp(
                                `${idPrefix}dashboard-sidebar-profile-role`,
                            )}
                            id={`${idPrefix}dashboard-sidebar-profile-role`}
                        >
                            {profileRole}
                        </span>
                    </div>
                </div>
            </Sidebar.Header>
            <Sidebar.Content
                data-testid={`${idPrefix}dashboard-sidebar-content`}
                id={`${idPrefix}dashboard-sidebar-content`}
            >
                <Sidebar.Group
                    data-testid={`${idPrefix}dashboard-sidebar-group`}
                    id={`${idPrefix}dashboard-sidebar-group`}
                >
                    <Sidebar.Menu
                        aria-label={t('layout.aria.navigation')}
                        data-testid={`${idPrefix}dashboard-sidebar-menu`}
                        defaultExpandedKeys={defaultExpandedKeys}
                        id={`${idPrefix}dashboard-sidebar-menu`}
                    >
                        {visibleItems.map((item) => (
                            <SidebarNavItem
                                idPrefix={idPrefix}
                                item={item}
                                key={`${idPrefix}${item.href}`}
                                pathname={pathname}
                            />
                        ))}
                        {SECURED_NAVIGATION_GROUPS.map((group) => (
                            <SidebarNavGroup
                                group={group}
                                idPrefix={idPrefix}
                                key={`${idPrefix}${group.slug}`}
                                pathname={pathname}
                            />
                        ))}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer
                data-testid={`${idPrefix}dashboard-sidebar-footer`}
                id={`${idPrefix}dashboard-sidebar-footer`}
            >
                {showThemeToggle ? (
                    <>
                        <div
                            className="flex items-center justify-between gap-3 px-1 py-1"
                            {...getNodeTestIdProp(
                                `${idPrefix}dashboard-sidebar-language-row`,
                            )}
                            id={`${idPrefix}dashboard-sidebar-language-row`}
                        >
                            <span
                                className="text-sm font-medium text-foreground"
                                {...getNodeTestIdProp(
                                    `${idPrefix}dashboard-sidebar-language-label`,
                                )}
                                id={`${idPrefix}dashboard-sidebar-language-label`}
                            >
                                {t('layout.language.label')}
                            </span>
                            <DashboardLanguageToggle
                                className="gap-0"
                                id={`${idPrefix}dashboard-sidebar-language-toggle`}
                                itemIdPrefix={`${idPrefix}dashboard-sidebar`}
                            />
                        </div>
                        <div
                            className="flex items-center justify-between gap-3 px-1 py-1"
                            {...getNodeTestIdProp(
                                `${idPrefix}dashboard-sidebar-theme-row`,
                            )}
                            id={`${idPrefix}dashboard-sidebar-theme-row`}
                        >
                            <span
                                className="text-sm font-medium text-foreground"
                                {...getNodeTestIdProp(
                                    `${idPrefix}dashboard-sidebar-theme-label`,
                                )}
                                id={`${idPrefix}dashboard-sidebar-theme-label`}
                            >
                                {t('layout.theme.label')}
                            </span>
                            <DashboardThemeToggle
                                className="gap-0"
                                id={`${idPrefix}dashboard-sidebar-theme-toggle`}
                                itemIdPrefix={`${idPrefix}dashboard-sidebar`}
                            />
                        </div>
                    </>
                ) : null}
                <Sidebar.Menu
                    aria-label={t('layout.aria.account')}
                    data-testid={`${idPrefix}dashboard-sidebar-footer-menu`}
                    id={`${idPrefix}dashboard-sidebar-footer-menu`}
                >
                    <Sidebar.MenuItem
                        data-testid={`${idPrefix}dashboard-sidebar-logout`}
                        id={`${idPrefix}dashboard-sidebar-logout`}
                        onAction={onLogout}
                        textValue={t('layout.actions.logOut')}
                    >
                        <Sidebar.MenuIcon
                            data-testid={`${idPrefix}dashboard-sidebar-logout-icon-wrap`}
                            id={`${idPrefix}dashboard-sidebar-logout-icon-wrap`}
                        >
                            <Icon
                                className="size-4"
                                data-testid={`${idPrefix}dashboard-sidebar-logout-icon`}
                                icon="solar:logout-3-linear"
                                id={`${idPrefix}dashboard-sidebar-logout-icon`}
                            />
                        </Sidebar.MenuIcon>
                        <Sidebar.MenuLabel
                            data-testid={`${idPrefix}dashboard-sidebar-logout-label`}
                            id={`${idPrefix}dashboard-sidebar-logout-label`}
                        >
                            {t('layout.actions.logOut')}
                        </Sidebar.MenuLabel>
                        <Sidebar.MenuChip
                            data-testid={`${idPrefix}dashboard-sidebar-logout-chip-wrap`}
                            id={`${idPrefix}dashboard-sidebar-logout-chip-wrap`}
                        >
                            <Chip
                                color="danger"
                                data-testid={`${idPrefix}dashboard-sidebar-logout-chip`}
                                id={`${idPrefix}dashboard-sidebar-logout-chip`}
                                size="sm"
                                variant="soft"
                            >
                                {t('layout.actions.exit')}
                            </Chip>
                        </Sidebar.MenuChip>
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1">
                    <BuildCommit />
                    <ModeBadge />
                </div>
            </Sidebar.Footer>
        </>
    );
};

const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
    group,
    idPrefix,
    pathname,
}) => {
    const { t } = useTranslation('components');
    const label = t(group.labelKey);
    const groupItemId = `${idPrefix}dashboard-sidebar-group-item-${group.slug}`;

    return (
        <Sidebar.MenuItem
            data-testid={groupItemId}
            id={groupItemId}
            textValue={label}
        >
            <Sidebar.MenuIcon
                data-testid={`${groupItemId}-icon-wrap`}
                id={`${groupItemId}-icon-wrap`}
            >
                <Icon
                    className="size-4"
                    data-testid={`${groupItemId}-icon`}
                    icon={group.icon}
                    id={`${groupItemId}-icon`}
                />
            </Sidebar.MenuIcon>
            <Sidebar.MenuLabel
                data-testid={`${groupItemId}-label`}
                id={`${groupItemId}-label`}
            >
                {label}
            </Sidebar.MenuLabel>
            <Sidebar.MenuTrigger
                data-testid={`${groupItemId}-trigger`}
                id={`${groupItemId}-trigger`}
            >
                <Sidebar.MenuIndicator
                    data-testid={`${groupItemId}-indicator`}
                    id={`${groupItemId}-indicator`}
                />
            </Sidebar.MenuTrigger>
            <Sidebar.Submenu>
                {group.items.map((item) => (
                    <SidebarNavItem
                        idPrefix={idPrefix}
                        item={item}
                        key={`${idPrefix}${item.href}`}
                        pathname={pathname}
                    />
                ))}
            </Sidebar.Submenu>
        </Sidebar.MenuItem>
    );
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
    idPrefix,
    item,
    pathname,
}) => {
    const { t } = useTranslation('components');
    const { isMobile, setMobileOpen } = useSidebar();
    // The shared resolver, not a second copy of the matching rule (BOS-170).
    // A nested route is matched by its parent section as well as by itself, so
    // "does this item match" highlighted two rows at once on the old
    // /sales/shifts, and the navbar — a different function — titled it "SALES".
    // One answer, read by both.
    const isCurrent = findActiveNavigationItem(pathname)?.slug === item.slug;

    const label = t(item.labelKey);
    const slug = item.slug;
    const itemId = `${idPrefix}dashboard-sidebar-item-${slug}`;

    // The `Link` is the **only** navigator (BOS-171).
    //
    // The row used to carry a second navigator on its `Sidebar.MenuItem`, and
    // a click was kept to one navigation by `stopPropagation()` on the anchor.
    // That does not work: react-aria's `usePress` listens to *pointer* events,
    // not the bubbling click, so stopping the click stops nothing it does — and
    // both handlers firing means two history entries and a Back button that
    // needs pressing twice.
    //
    // Removing `onAction` removes the second path rather than trying to
    // suppress it. Nothing is lost: the row contains a real `<a href>`, which
    // is what makes it keyboard-operable (Enter activates a link), middle- and
    // ctrl-clickable into a new tab, and hoverable with a visible destination
    // in the status bar. `Link` also declines to navigate on a modified click
    // by itself, which is what the hand-rolled modifier branch here used to do.
    //
    // What is left is the part `Link` cannot know: closing the mobile drawer,
    // and only on a click that is actually going somewhere.
    const onLinkClick = React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            const isModified =
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey;

            if (!isModified && isMobile) {
                setMobileOpen(false);
            }
        },
        [isMobile, setMobileOpen],
    );

    return (
        <Sidebar.MenuItem
            data-testid={itemId}
            id={itemId}
            isCurrent={isCurrent}
            textValue={label}
        >
            <Link
                className="flex min-w-0 flex-1 items-center gap-3 text-inherit no-underline"
                data-testid={`${itemId}-link`}
                id={`${itemId}-link`}
                onClick={onLinkClick}
                to={item.href}
            >
                <Sidebar.MenuIcon
                    data-testid={`${itemId}-icon-wrap`}
                    id={`${itemId}-icon-wrap`}
                >
                    <Icon
                        className="size-4"
                        data-testid={`${itemId}-icon`}
                        icon={item.icon}
                        id={`${itemId}-icon`}
                    />
                </Sidebar.MenuIcon>
                <Sidebar.MenuLabel
                    data-testid={`${itemId}-label`}
                    id={`${itemId}-label`}
                >
                    {label}
                </Sidebar.MenuLabel>
                {item.badge ? (
                    <Sidebar.MenuChip
                        data-testid={`${itemId}-chip-wrap`}
                        id={`${itemId}-chip-wrap`}
                    >
                        <Chip
                            color="success"
                            data-testid={`${itemId}-chip`}
                            id={`${itemId}-chip`}
                            size="sm"
                            variant="soft"
                        >
                            {t(item.badge)}
                        </Chip>
                    </Sidebar.MenuChip>
                ) : null}
            </Link>
        </Sidebar.MenuItem>
    );
};
